import React, { useEffect, useContext } from "react";
import classNames from "classnames";
import PropTypes from "prop-types";
import StoreDataContext from "src/app/context/store-data";
import sdkProvider from "src/utilities/sdkProvider";
import noticeError from "src/app/utilities/noticeError";
import useScriptTag from "src/app/hooks/useScriptTag";
import useLinkTag from "src/app/hooks/useLinkTag";
import getCustomerAccessToken from "src/app/utilities/getCustomerAccessToken";
import ChatBotIdentityErrorMessage from "src/app/components/ChatBotIdentityErrorMessage";
import ChatBotNuanceErrorMessage from "src/app/components/ChatBotNuanceErrorMessage";
import initialiseNuance from "./helpers/initialiseNuance";
import styles from "./styles.module.css";

export default function ChatBot({ status, isActive, onStatusChange }) {
  const {
    nuanceDataCenter,
    nuanceTagServerName,
    nuanceVendorUUID,
    nuanceAssetsDomain,
  } = useContext(StoreDataContext);
  const sdkScriptSrc = `${nuanceAssetsDomain}/nuan_websdk_bootstrap.min.0.0.2.js`;
  const sdkLinkSrc = `${nuanceAssetsDomain}/nuance-chat.min.0.0.2.css`;
  const c2cSciptSrc = `${nuanceAssetsDomain}/nuan-c2c.0.0.2.js`;
  const c2cLinkSrc = `${nuanceAssetsDomain}/nuance-c2c-button.0.0.2.css`;
  const sdkScriptTagStatus = useScriptTag(sdkScriptSrc);
  const sdkLinkTagStatus = useLinkTag(sdkLinkSrc);
  const c2cScriptStatus = useScriptTag(c2cSciptSrc);
  const c2cLinkStatus = useLinkTag(c2cLinkSrc);
  const assetsLoaded = [
    sdkScriptTagStatus,
    sdkLinkTagStatus,
    c2cScriptStatus,
    c2cLinkStatus,
  ].every((value) => value === "ready");

  const buttonClasses = classNames({
    [`${styles.hidden}`]: isActive,
  });
  const frameClasses = classNames({
    [`${styles.chatFrame}`]: true,
    [`${styles.hidden}`]: !isActive,
  });

  async function onMessage(event) {
    const origins = [
      "https://asos.digital.nuance.com",
      "https://asos-preprod.digital.nuance.com",
      "https://asos-stage.digital.nuance.com",
    ];

    if (origins.indexOf(event.origin) < 0) return;

    const data = JSON.parse(event.data);
    const loginTrigger = data?.trigger === "AsosLogin";

    if (loginTrigger) {
      const identity = await sdkProvider("identity");

      identity.login();
    }
  }

  useEffect(() => {
    async function init() {
      if (assetsLoaded) {
        try {
          const accessToken = await getCustomerAccessToken();
          const identity = await sdkProvider("identity");
          const customerId = await identity.customer.customerId();
          const isAuthenticated = await identity.customer.isAuthenticated();
          const firstName = await identity.customer.firstName();
          const lastName = await identity.customer.lastName();
          const browseCountry = window.asos.webContext.getBrowseCountry();
          const browseLanguage = window.asos.webContext.getBrowseLanguage();
          const floor = window.asos.webContext.getFloor();
          const storeCode = window.asos.webContext.getStoreId();
          const referrer = document.referrer;

          const nuanceData = {
            accessToken,
            browseCountry,
            browseLanguage,
            customerId,
            firstName,
            lastName,
            floor,
            isAuthenticated,
            referrer,
            storeCode,
          };

          await initialiseNuance(
            nuanceDataCenter,
            nuanceTagServerName,
            nuanceVendorUUID,
            nuanceData,
            onStatusChange
          );
        } catch (error) {
          noticeError(error);
          const errorType = error.message.toLowerCase().replaceAll(" ", "-");
          onStatusChange(errorType);
        }
      }
    }

    init();

    window.addEventListener("message", onMessage, false);

    return () => {
      window.removeEventListener("message", onMessage);
    };
  }, [assetsLoaded]);

  if (status === "identity-timeout") {
    return <ChatBotIdentityErrorMessage />;
  }

  if (status === "nuance-timeout") {
    return <ChatBotNuanceErrorMessage />;
  }

  return (
    <>
      <div
        id="nuance-chat-fixed"
        data-testid="chat-bot"
        className={buttonClasses}
      />
      <div id="nuanMessagingFrame" className={frameClasses} />
    </>
  );
}

ChatBot.displayName = "ChatBot";

ChatBot.propTypes = {
  isActive: PropTypes.bool.isRequired,
  onStatusChange: PropTypes.func.isRequired,
  status: PropTypes.string.isRequired,
};

ChatBot.defaultProps = {
  isActive: false,
};
